import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantSubscriptionService } from "@/services/tenant-subscription-service";
import { hotelKeys } from "@/components/pages/hotels/hooks/useHotels";
import { tenantCatalogKeys } from "@/components/pages/tenants/hooks/useTenants";

interface UseHotelSubscriptionOptions {
  refetchInterval?: number | false;
}

export const hotelSubscriptionKeys = {
  all: ["hotelSubscriptions"] as const,
  plans: () => [...hotelSubscriptionKeys.all, "plans"] as const,
  tenantSubscriptions: () =>
    [...hotelSubscriptionKeys.all, "tenantSubscriptions"] as const,
  tenantTransactions: () =>
    [...hotelSubscriptionKeys.all, "tenantTransactions"] as const,
  tenantTransactionList: (subscriptionId: string) =>
    [...hotelSubscriptionKeys.tenantTransactions(), subscriptionId] as const,
  details: () => [...hotelSubscriptionKeys.all, "detail"] as const,
  detail: (hotelId: string) =>
    [...hotelSubscriptionKeys.details(), hotelId] as const,
};

export const useTenantSubscriptionPlans = (enabled: boolean) =>
  useQuery({
    queryKey: hotelSubscriptionKeys.plans(),
    queryFn: () => tenantSubscriptionService.getPlans(),
    staleTime: 60 * 1000,
    enabled,
  });

export const useMyTenantSubscriptions = (enabled: boolean) =>
  useQuery({
    queryKey: hotelSubscriptionKeys.tenantSubscriptions(),
    queryFn: () => tenantSubscriptionService.getMySubscriptions(),
    staleTime: 60 * 1000,
    enabled,
  });

export const useMySubscriptionTransactions = (
  subscriptionId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: subscriptionId
      ? hotelSubscriptionKeys.tenantTransactionList(subscriptionId)
      : hotelSubscriptionKeys.tenantTransactions(),
    queryFn: () =>
      tenantSubscriptionService.getMySubscriptionTransactions(
        subscriptionId ?? "",
        {
          limit: 20,
          offset: 0,
        },
      ),
    staleTime: 60 * 1000,
    enabled: enabled && subscriptionId !== null,
  });

export const useHotelSubscription = (
  hotelId: string | null,
  enabled: boolean,
  options?: UseHotelSubscriptionOptions,
) =>
  useQuery({
    queryKey: hotelId
      ? hotelSubscriptionKeys.detail(hotelId)
      : hotelSubscriptionKeys.details(),
    queryFn: () =>
      tenantSubscriptionService.getHotelSubscription(hotelId ?? ""),
    staleTime: 60 * 1000,
    enabled: enabled && hotelId !== null,
    refetchInterval: options?.refetchInterval ?? false,
  });

export const useAttachHotelSubscriptionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotelId,
      planId,
      roomsNumber,
    }: {
      hotelId: string;
      planId: string;
      roomsNumber?: number;
    }) =>
      tenantSubscriptionService.attachHotelPlan(hotelId, planId, roomsNumber),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: hotelSubscriptionKeys.detail(variables.hotelId),
      });
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.all });
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useSubscriptionPaymentUrl = () =>
  useMutation({
    mutationFn: ({
      subscriptionId,
      returnUrl,
    }: {
      subscriptionId: string;
      returnUrl: string;
    }) => tenantSubscriptionService.getPaymentUrl(subscriptionId, returnUrl),
  });

export const useSubscriptionPortalUrl = () =>
  useMutation({
    mutationFn: (returnUrl: string) =>
      tenantSubscriptionService.getPortalUrl(returnUrl),
  });

export const useDetachHotelSubscriptionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) =>
      tenantSubscriptionService.detachHotelPlan(hotelId),
    onSuccess: (_response, hotelId) => {
      queryClient.invalidateQueries({
        queryKey: hotelSubscriptionKeys.detail(hotelId),
      });
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.all });
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useToggleHotelSubscriptionFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotelId,
      featureKey,
      enabled,
    }: {
      hotelId: string;
      featureKey: string;
      enabled: boolean;
    }) =>
      tenantSubscriptionService.toggleHotelFeature(
        hotelId,
        featureKey,
        enabled,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: hotelSubscriptionKeys.detail(variables.hotelId),
      });
      queryClient.invalidateQueries({
        queryKey: ["selectedHotelSubscription", variables.hotelId],
      });
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};
